import type { AxiosResponse } from 'axios';
import type {
  EnumAllowed,
  LinkBlockItemModel,
  ModelCategory,
  ModelOfferItem,
  ModelSpecification,
  ModelVideo,
  OfferModel,
  ResponseModel,
  ResponseModelBaseSpecs,
  ResponseModelSimilar,
} from '~/interfaces/api';

interface RangeFilteringValue {
  min: number;
  max: number;
}

type FilterReturn = undefined | boolean | number[] | RangeFilteringValue;
type queryType = null | string | undefined;

interface BodyFilters {
  id: number;
  value: FilterReturn;
}

interface BrandsFilterValue {
  value: number[];
  query?: queryType;
}

interface ShopsFilterValue {
  value: number[];
  query?: queryType;
}

interface ImageSize {
  width: number;
  height: number;
}

interface PostBodyFilters {
  filters: BodyFilters[];
  price?: RangeFilteringValue;
  discount?: 1 | 0;
  best_price?: 1 | 0;
  brand_id?: number[];
  shop_ids?: number[];
  thereIsData?: boolean;
}

interface PaginationElement {
  class: string;
  value: string;
  key?: string;
  disabled?: boolean;
}

interface ScrollPos {
  value: number;
  width?: number;
}

interface IItems {
  name: string;
  title?: string;
  slug: string;
  is_active: boolean;
  selected: boolean;
}

interface IBuiltInMemory {
  type?: string;
  label?: string;
  name?: string;
  items?: IItems[];
  selected?: boolean;
}

interface IBuiltInMemoryProps extends IBuiltInMemory {
  data?: IBuiltInMemory[];
  addFilterQuery?: string;
  value?: {
    items?: IItems[] | undefined[];
  };
}

interface CatalogInApp {
  title: string;
  url: string;
  image?: string;
  expand_title?: string;
  children?: CatalogInApp[];
  banners?: CatalogInApp[];
  logos?: CatalogInApp[];
}

interface resultIntroSearchItem {
  name: string;
  id: Number;
  product_slug: string;
  coupons_slug?: string;
  slug?: string;
}

interface inputHintSuggest {
  startString: string;
  endString: string;
  searchInString: string;
}

interface Region {
  id?: number;
  name?: string | null;
  subdomain?: string | null;
  parents?: string[];
}

interface RegionInAppChange extends Region {
  needQuestion?: boolean;
  needRedirect?: boolean;
}

interface RegionInApp extends RegionInAppChange {}

declare global {
  interface Window {
    ymaps: any;
    googlePaymentsClient: any;
    ApplePaySession: any;
    yaContextCb: any;
    Ya: any;
    DSPCounter: any;
    dataLayer: any;
    gtag: any;
    vkAsyncInit: any;
    ym: any;
    VK: any;
    _uxsSettings: undefined | { id: string };
    rrPartnerId: string;
    rrApi: any;
    rrApiOnReady: any[];
    setFavorite: (id: string, catId: string, key: string) => void;
    rcm1x: any;
    debugSessionData: any;
    smartCaptcha: any;
  }
}

interface SelectedFilters {
  [propName: string]: {
    value: FilterReturn;
    query: queryType;
  };
}

interface PriceFilterValue {
  value: RangeFilteringValue;
  query?: queryType;
}

interface ActiveFilter {
  name: string;
  filter:
    | 'price'
    | 'brand'
    | 'shop'
    | 'discount'
    | 'best_price'
    | 'filter'
    | 'available'
    | 'category';
  id: number;
  item_id?: number;
}

interface filtersValues {
  filters: SelectedFilters;
  discount: boolean;
  best_price: boolean;
  price: PriceFilterValue | null;
  brands: BrandsFilterValue | null;
  shops: ShopsFilterValue | null;
  active: ActiveFilter[] | null;
  initiated: boolean;
  reqBody: PostBodyFilters;
}

interface GetCategoryRatings {
  best_rating?: number;
  rating_value?: number;
  review_count?: number;
  worst_rating?: number;
}

interface SelectedCategoryInfo {
  [key: string]: any;

  id: number;
  adult: boolean;
}

interface ListingsGetCategory {
  categories: ModelCategory[];
  selected?: SelectedCategoryInfo;
  cancelToken?: boolean;
  items?: ModelOfferItem[];
}

interface SortedProducts {
  [key: string]: ResponseModel[];
}

interface DataCompare extends ListingsGetCategory {
  selectedCategory: ModelCategory;
  productsByCategory: SortedProducts[];
}

interface FiltersChangeArguments {
  id: string;
  val: FilterReturn;
  queryVal: queryType;
}

interface SuggestSearchItem {
  id: String;
  name: String;
  slug: String;
  title?: String;
}

interface SuggestResponse {
  categories?: SuggestSearchItem[];
  models?: SuggestSearchItem[];
  presets?: SuggestSearchItem[];
  search_queries?: SuggestSearchItem[];
}

interface Sections {
  type: string;
  layout: string;
}

interface FavoriteCategories {
  [categoryId: string]: string;
}

interface FavoriteFilters {
  categories: number[];
  available: boolean;
}

enum FavoriteFiltersTypes {
  AVAILABLE = 'available',
  CATEGORIES = 'categories',
}

interface ModelManySpecifications {
  title: string;
  description: string;
  value: (string | null)[];
  different: boolean;
}

interface groupManySpecifications {
  group: string;
  specifications: ModelManySpecifications[];
}

interface sortedSpecifications {
  [key: string]: groupManySpecifications[];
}

interface AnchorsCell {
  id: number;
  name: string;
  slug: string;
  coupon_count: number;

  [key: string]: any;
}

interface Anchors {
  [key: string]: AnchorsCell[];
}

interface TargetHTMLAnchorElement extends HTMLAnchorElement {
  value: string;
}

interface PaginationElementTarget {
  key?: string;
  value: string;
  target: TargetHTMLAnchorElement;
}

interface PageDataModel extends ResponseModel {
  baseSpec?: ResponseModelBaseSpecs;
  class?: string;
  model: ResponseModel;
  presets?: LinkBlockItemModel[];
  position?: number;
  offers?: {
    page: number;
    per_page: number;
    total: number;
    list: OfferModel[];
    offer_count: number;
    price_min: number;
    price_max: number;
    last_modified: null;
  };
  pictures?: string[];
  videos?: ModelVideo[];
  popularOffer?: ModelOfferItem;
  active?: string;
  brandPresets?: LinkBlockItemModel[];
  specifications?: ModelSpecification[];
  similar?: ResponseModelSimilar[];
}

interface ParamsReviewsQuestions {
  per_page: number;
  page: number;
  external: string | boolean;
  region_id: number;
  device?: string | number;
  tbp: string;
}

interface StoreModel {
  isBot: boolean;
  region: RegionInApp;
  device?: string | number;
  sem: string;
  additional?: {
    split: string;
  };
  debugPanelAll?: string | boolean;
  adult?: boolean;
  split?: boolean;
  updateSeoTexts?: (data: { [p: string]: string }) => { [p: string]: string };
  analytics?: string;
  analyticsData?: string;
}

interface ItemFilter {
  id: number;
  type: string;
  unit: string;
  description: string;
  name: string;
  allowed_values?: EnumAllowed[];
}

interface FiltersListings {
  price: string;
  id: number;
  shops?: {
    id?: number;
    name: string;
  }[];
  brands: { name: string }[];
  filters: ItemFilter[];
  count: number;
  best_price?: {
    name: string;
    filter: boolean;
  };
}

interface FeetBackModalItem {
  model_id: number;
  score: number;
  author: string;
  email: string;
  pros: string;
  cons: string;
  extract: string;
  date: string;
  photo_ids: string[] | unknown;
}

interface ReviewAxiosResponse extends AxiosResponse {
  id?: number;
}

interface FileResponse extends File {
  id?: number;
}

interface compareItem {
  id: number;
  catId: string | number;
  deleteCard: boolean;
  hideNotification?: boolean;
}

interface PSelect {
  id: number;
  name: string;
  value: string;
}

interface Modals {
  name: String;
  data: any;
}

interface BlockUser {
  text: string;
  tableData?: {
    titleList: string[];
    textList: { category: string; list: string[]; count: string }[];
  };
  list?: BlockUser[];
}

interface internalValue {
  min: number | null;
  max: number | null;
}

interface styleHover {
  top: string;
  height: string;
}

interface HintInterface {
  description: string;
  cornerStyle: string;
  style: string;
  key: string;
}

interface Arrows {
  show: boolean;
  left: boolean;
  right: boolean;
}

interface Storage {
  [key: string]: {
    index: number;
    spec: {
      [key: string]: number;
    };
  };
}

interface SpecStorage {
  [key: string]: Storage;
}

export type {
  ModelManySpecifications,
  groupManySpecifications,
  sortedSpecifications,
  FavoriteCategories,
  FavoriteFilters,
  Sections,
  SuggestSearchItem,
  SuggestResponse,
  RegionInApp,
  filtersValues,
  GetCategoryRatings,
  ListingsGetCategory,
  SelectedCategoryInfo,
  ActiveFilter,
  FiltersChangeArguments,
  RegionInAppChange,
  inputHintSuggest,
  resultIntroSearchItem,
  RangeFilteringValue,
  FilterReturn,
  queryType,
  BodyFilters,
  PostBodyFilters,
  BrandsFilterValue,
  ShopsFilterValue,
  ImageSize,
  PaginationElement,
  ScrollPos,
  IBuiltInMemory,
  CatalogInApp,
  Anchors,
  AnchorsCell,
  TargetHTMLAnchorElement,
  PaginationElementTarget,
  PageDataModel,
  ParamsReviewsQuestions,
  StoreModel,
  FiltersListings,
  ItemFilter,
  FeetBackModalItem,
  ReviewAxiosResponse,
  FileResponse,
  compareItem,
  IItems,
  IBuiltInMemoryProps,
  PSelect,
  DataCompare,
  Modals,
  BlockUser,
  internalValue,
  styleHover,
  HintInterface,
  Arrows,
  SortedProducts,
  Storage,
  SpecStorage,
};
export { FavoriteFiltersTypes };
